import React from 'react';

import {isAnalysisTemplatesEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import CreateEmptyAnalysisTemplateModal from 'analysis-tool/templates/CreateEmptyAnalysisTemplateModal';
import {AssaysFeature, RecipesFeature, V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import CreateFormModal from 'forms/form-definition/CreateFormModal';
import {isWorkflowCreateTaskFormsEnabled} from 'forms/FormsPermissions';
import {ActionOrigin, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {CollectionPageTemplateCollectionNode} from 'notebook/entry-templates/collections/TemplateCollectionTable';
import CreateTemplateModal, {CreateSubtemplateModal} from 'notebook/entry-templates/CreateTemplateModal';
import SubtemplateUtil from 'notebook/entry-templates/SubtemplateUtil';
import * as TemplateConstants from 'notebook/entry-templates/TemplateConstants';
import TemplateUtil from 'notebook/entry-templates/TemplateUtil';
import {TEMPLATE_COLLECTIBLE_TYPES} from 'notebook/template-collections/TemplateCollectionTypes';
import * as ProcedureDAO from 'procedures/network/ProcedureDAO';
import {serializeProcedureFlowchartForNetwork} from 'procedures/network/ProcedureNetworkTranslators';
import getProcedureUIString, {ProceduresStringKeys} from 'procedures/ProceduresStringKeys';
import CreateProcedureModal from 'procedures/templates/CreateProcedureModal';
import CreateRequestV2TemplateModal from 'processes/requests_v2/modals/CreateRequestV2TemplateModal';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import * as ModalContainer from 'ui-library/ModalContainer';

export const TEMPLATE = 'Entry template';
export const SUBTEMPLATE = 'Sub-template';
export const FORM_DEFINITION = 'Form';
export const ANALYSIS_TEMPLATE = 'Analysis template';
export const RECIPE = getProcedureUIString(ProceduresStringKeys.PROCEDURE, ProcedureType.RECIPE);
export const ASSAY = getProcedureUIString(ProceduresStringKeys.PROCEDURE, ProcedureType.ASSAY);
export const REQUEST_V2_DEFINITION = 'Request template';
const MODAL_CONTAINER_KEY = 'TEMPLATE_COLLECTION_MODAL_CONTAINER_KEY';

type TemplateTypesArray = Array<{
  /**
   * The string that we should use in the UI to display this Template Type.
   */
  name: string;

  /**
   * The ModelTypeConstant that corresponds to this Template Type.
   */
  value: ModelTypeConstants;

  /**
   * The Template Type in string format that is used by TemplateCollectionNode query
   */
  filterValue: string;

  /**
   * Can these be created by the creation button
   */
  isCreatable: () => boolean;

  /**
   * Can these be filtered in the template collection sidebar and page
   */
  isViewable: () => boolean;

  /**
   * This click handler is triggered from the Template Collection page and indicates that we want to create some instance of the given type.
   * Typically, this should open some modal where we can get additional information.
   */
  createTemplateTypeClickHandler: () => void;
}>;

const TemplateCollectionUtil = {
  isTemplateCollectibleModelType(modelType: string): boolean {
    return TEMPLATE_COLLECTIBLE_TYPES.includes(modelType as ModelTypeConstants);
  },
  getTemplateCollectionTypes(collection: CollectionPageTemplateCollectionNode): TemplateTypesArray {
    return [
      {
        name: TEMPLATE,
        value: ModelTypeConstants.TEMPLATE,
        filterValue: ModelTypeConstants.TEMPLATE,
        isCreatable: () => true,
        isViewable: () => true,
        createTemplateTypeClickHandler: () => {
          createTemplateClickHandler(collection);
        },
      },
      {
        name: SUBTEMPLATE,
        value: ModelTypeConstants.SUBTEMPLATE,
        filterValue: ModelTypeConstants.SUBTEMPLATE,
        isCreatable: () => true,
        isViewable: () => true,
        createTemplateTypeClickHandler: () => {
          createSubtemplateClickHandler(collection);
        },
      },
      {
        name: ANALYSIS_TEMPLATE,
        value: ModelTypeConstants.ANALYSIS_TEMPLATE,
        filterValue: ModelTypeConstants.ANALYSIS_TEMPLATE,
        isCreatable: () =>
          isAnalysisTemplatesEnabled() && BenchlingConfig.ENABLE_ANALYSIS_TOOL_EDITABLE_TEMPLATES,
        isViewable: () => BenchlingConfig.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS,
        createTemplateTypeClickHandler: () => {
          createAnalysisTemplateClickHandler(collection);
        },
      },
      {
        name: FORM_DEFINITION,
        value: ModelTypeConstants.FORM_DEFINITION,
        filterValue: ModelTypeConstants.FORM_DEFINITION,
        isCreatable: () => isWorkflowCreateTaskFormsEnabled(),
        isViewable: () => isWorkflowCreateTaskFormsEnabled(),
        createTemplateTypeClickHandler: () => {
          createFormClickHandler(collection);
        },
      },
      {
        name: REQUEST_V2_DEFINITION,
        value: ModelTypeConstants.REQUEST_V2_DEFINITION,
        filterValue: ModelTypeConstants.REQUEST_V2_DEFINITION,
        isCreatable: () => isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_ADMIN}),
        isViewable: () => isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_OPEN}),
        createTemplateTypeClickHandler: () => {
          createRequestV2TemplateClickHandler(collection);
        },
      },
      {
        name: RECIPE,
        value: ModelTypeConstants.RECIPE_PROCEDURE,
        filterValue: 'recipe',
        isCreatable: () => isFeatureEnabled({feature: RecipesFeature.RECIPES_CREATE_RECIPE}),
        isViewable: () => isFeatureEnabled({feature: RecipesFeature.RECIPES_OPEN}),
        createTemplateTypeClickHandler: () => {
          createProcedureClickHandler(ProcedureType.RECIPE, collection);
        },
      },
      {
        name: ASSAY,
        value: ModelTypeConstants.ASSAY_PROCEDURE,
        filterValue: 'assay',
        isCreatable: () => isFeatureEnabled({feature: AssaysFeature.ASSAYS_CREATE_ASSAY}),
        isViewable: () => isFeatureEnabled({feature: AssaysFeature.ASSAYS_OPEN}),
        createTemplateTypeClickHandler: () => {
          createProcedureClickHandler(ProcedureType.ASSAY, collection);
        },
      },
    ];
  },
};

function handleSubmit(
  name: string,
  templateCollection: TemplateConstants.TemplateCollection,
  isProtected: boolean,
  tagSchema?: TagSchema,
  schemaTags?: TagUpdateObject
): void {
  TemplateUtil.createThenOpen(name, templateCollection.id, isProtected, tagSchema, schemaTags).done(() => {
    ModalContainer.close(MODAL_CONTAINER_KEY);
  });
}

function handleSubmitSubtemplate(
  name: string,
  templateCollection: TemplateConstants.TemplateCollection
): void {
  SubtemplateUtil.createThenOpen(name, templateCollection.id).then(() => {
    ModalContainer.close(MODAL_CONTAINER_KEY);
  });
}

function createTemplateClickHandler(collection: CollectionPageTemplateCollectionNode): void {
  if (!collection) {
    return;
  }
  ModalContainer.open(
    MODAL_CONTAINER_KEY,
    <CreateTemplateModal
      onRequestClose={() => {
        ModalContainer.close(MODAL_CONTAINER_KEY);
      }}
      onSubmit={handleSubmit}
      initialTemplateCollection={collection}
    />
  );
}

function createSubtemplateClickHandler(collection: CollectionPageTemplateCollectionNode): void {
  if (!collection) {
    return;
  }
  ModalContainer.open(
    MODAL_CONTAINER_KEY,
    <CreateSubtemplateModal
      onRequestClose={() => {
        ModalContainer.close(MODAL_CONTAINER_KEY);
      }}
      onSubmit={handleSubmitSubtemplate}
      initialTemplateCollection={collection}
    />
  );
}

function createFormClickHandler(collection: CollectionPageTemplateCollectionNode): void {
  if (!collection) {
    return;
  }
  ModalContainer.open(
    MODAL_CONTAINER_KEY,
    <CreateFormModal
      onRequestClose={() => {
        ModalContainer.close(MODAL_CONTAINER_KEY);
      }}
      initialTemplateCollection={collection}
    />
  );
}

function createRequestV2TemplateClickHandler(collection: CollectionPageTemplateCollectionNode): void {
  if (!collection) {
    return;
  }
  ModalContainer.open(
    MODAL_CONTAINER_KEY,
    <CreateRequestV2TemplateModal
      onRequestClose={() => {
        ModalContainer.close(MODAL_CONTAINER_KEY);
      }}
      initialTemplateCollection={collection}
      actionOrigin={ActionOrigin.NOTEBOOK_TEMPLATE_COLLECTION_PAGE}
    />
  );
}

function createProcedureClickHandler(
  procedureType: ProcedureType,
  collection: CollectionPageTemplateCollectionNode
): void {
  if (!collection) {
    return;
  }
  ModalContainer.open(
    MODAL_CONTAINER_KEY,
    <CreateProcedureModal
      onRequestClose={() => {
        ModalContainer.close(MODAL_CONTAINER_KEY);
      }}
      initialTemplateCollection={collection}
      procedureType={procedureType}
      onCreateProcedure={async (newProcedure) => {
        const {name, templateCollectionId, flowchart} = newProcedure;
        const {
          createdIds: [id],
        } = await ProcedureDAO.createProcedure({
          name,
          templateCollectionId,
          procedureType,
          flowchart: serializeProcedureFlowchartForNetwork(flowchart, procedureType),
        });
        return id;
      }}
    />
  );
}

function createAnalysisTemplateClickHandler(collection: CollectionPageTemplateCollectionNode): void {
  if (!collection) {
    return;
  }
  ModalContainer.open(
    MODAL_CONTAINER_KEY,
    <CreateEmptyAnalysisTemplateModal
      onRequestClose={() => {
        ModalContainer.close(MODAL_CONTAINER_KEY);
      }}
      initialTemplateCollection={collection}
    />
  );
}

export default TemplateCollectionUtil;
